/**
 * Decides the one thing the app treats as proof of a signed-in user: a Supabase session either
 * carries an access token, or it does not.
 *
 * It is a module of its own because the decision was unreachable where it lived. `getToken()`
 * needs a configured Supabase client and a live session before it can return anything, and
 * `onAuthChange()` needs a subscription — so the single line that defines "signed in" could not
 * be observed by a test, and the phase-14 sweep confirmed it: replacing `?? null` with
 * `?? "anonymous"` passed all 106 tests.
 *
 * It deliberately knows nothing about: the Supabase client or its response types, the network,
 * the 10-second timeout that may hand it `null` instead of a session, the route guard that acts
 * on the answer, and the extension handoff that persists it. It is given a value and answers
 * one question about it.
 *
 * The dangerous direction is not returning null too often — that only sends someone to a login
 * form, which is recoverable and obvious. It is returning a plausible non-token, because a fake
 * value PROPAGATES and every consumer of this decision is written as a truthiness check:
 *
 *   - `routes/_authenticated.tsx` lets the visitor into the full authenticated shell;
 *   - `routes/login.tsx` does the INVERSE check and redirects them away from `/login`, so there
 *     is genuinely no path back to a login form;
 *   - `components/LoginForm.tsx` treats the sign-in as complete;
 *   - `routes/__root.tsx` forwards the value to `chrome-extension://…#access_token=…`, where
 *     apps/extension writes it into `chrome.storage.local` and it outlives the tab;
 *   - every request then goes out as `Authorization: Bearer <placeholder>`.
 *
 * So the rule is narrow on purpose: only a non-blank string is a token. A blank string is not a
 * credential — it would be sent as the header `Bearer ` and pass every `!token` check on the
 * way there.
 */

/** The shape this needs from a Supabase session; anything else is simply not a session. */
interface SessionLike {
  access_token?: unknown;
}

/**
 * The rule itself, separated from where the value came from.
 *
 * `accessTokenFrom` reads it out of a session; `deriveAuthState` receives it already unwrapped
 * from `onAuthChange`'s callback and has no session to hand. Both must answer the same question
 * or "signed in" means two things one layer apart — so there is one rule and two entry points,
 * not two rules.
 */
export function usableToken(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return value.trim() ? value : null;
}

export function accessTokenFrom(session: unknown): string | null {
  return usableToken((session as SessionLike | null | undefined)?.access_token);
}

/** The three values `AuthListener` is called with, in the order it takes them. */
export type SessionIdentity = readonly [token: string | null, email: string | null, userId: string | null];

/** What this needs from a Supabase session beyond the token. */
interface SessionUserLike {
  user?: { email?: unknown; id?: unknown } | null;
}

/**
 * Everything `onAuthChange` reports about a session, produced in one place.
 *
 * `auth.ts` called its listener from four sites — the initial `getSession`, the
 * `onAuthStateChange` subscription, its catch handler, and the unconfigured-Supabase path — and
 * two of them built the triple by hand. Being a subscription callback on a configured Supabase
 * client, none of the four could be reached by a test, so a re-inlined `access_token ?? null`
 * there stayed green: measured, not assumed — reverting that one line to a hand-rolled read
 * failed nothing across the whole suite. That is what this closes. The rule for what counts as a
 * token is `usableToken`'s and is not restated here.
 *
 * A tuple rather than an object so the call site is `cb(...sessionIdentity(s))`: the three
 * arguments are all `string | null`, and in that shape a swapped email and user id is a silent
 * change of who the app thinks is signed in.
 */
export function sessionIdentity(session: unknown): SessionIdentity {
  const user = (session as SessionUserLike | null | undefined)?.user;
  const email = typeof user?.email === "string" ? user.email : null;
  const userId = typeof user?.id === "string" ? user.id : null;
  return [accessTokenFrom(session), email, userId];
}
